// ============================================
// Load Simulation Engine
// ============================================
//
// Configuration and orchestration for load testing
// the AI Nurse across 5 scaling tiers. Generates
// preset configs, estimates costs, and validates
// load configurations before execution.
// ============================================

import type {
  LoadSimConfig,
  LoadSimRun,
  PersonaMixConfig,
  ContextDistConfig,
} from '../../types/patientJourney';

// --- Tier Definitions ---

export const LOAD_TIERS: Record<
  LoadSimConfig['tier'],
  { label: string; minUsers: number; maxUsers: number; description: string }
> = {
  baseline: {
    label: 'Baseline',
    minUsers: 1,
    maxUsers: 50,
    description: 'Initial validation. Single-digit concurrency, full logging, manual review possible.',
  },
  growth: {
    label: 'Growth',
    minUsers: 50,
    maxUsers: 500,
    description: 'Early traction. Tests session management and basic concurrency patterns.',
  },
  surge: {
    label: 'Surge',
    minUsers: 500,
    maxUsers: 2000,
    description: 'Marketing spike. Tests auto-scaling, queue management, and degradation handling.',
  },
  viral: {
    label: 'Viral',
    minUsers: 2000,
    maxUsers: 10000,
    description: 'Viral growth event. Tests infrastructure limits, cost projections, and session leaks.',
  },
  stress: {
    label: 'Stress',
    minUsers: 10000,
    maxUsers: 100000,
    description: 'Breaking-point test. Finds infrastructure ceiling, measures graceful degradation.',
  },
};

// --- Default Persona Mix ---

function getDefaultPersonaMix(): PersonaMixConfig {
  return {
    cleanGate1: 40,
    drugInteraction: 10,
    gate2Escalation: 10,
    stateRestriction: 5,
    multiProtocol: 10,
    exclusionCriteria: 5,
    skeptic: 10,
    overOptimizer: 5,
    custom: 5,
  };
}

// --- Default Context Distribution ---

function getDefaultContextDist(): ContextDistConfig {
  return {
    timeOfDay: { morning: 30, afternoon: 35, evening: 25, late_night: 10 },
    stressLevel: { low: 30, moderate: 40, high: 20, crisis: 10 },
    healthKnowledge: { minimal: 15, basic: 40, informed: 30, expert: 15 },
    budgetSensitivity: { low: 25, moderate: 40, high: 25, severe: 10 },
  };
}

// --- Default Touchpoint Distribution ---

function getDefaultTouchpointDist(): LoadSimConfig['touchpointDistribution'] {
  return {
    firstContact: 50,
    midProtocol: 15,
    retest: 15,
    gate2: 10,
    cancellation: 10,
  };
}

// --- Default Load Configs ---

export function getDefaultLoadConfig(tier: LoadSimConfig['tier']): LoadSimConfig {
  const tierDef = LOAD_TIERS[tier];

  const concurrentUsers = Math.round((tierDef.minUsers + tierDef.maxUsers) / 2);

  const rampConfigs: Record<LoadSimConfig['tier'], { rampUp: number; sustain: number; rampDown: number }> = {
    baseline: { rampUp: 30, sustain: 300, rampDown: 30 },
    growth: { rampUp: 60, sustain: 600, rampDown: 60 },
    surge: { rampUp: 120, sustain: 900, rampDown: 120 },
    viral: { rampUp: 180, sustain: 1200, rampDown: 180 },
    stress: { rampUp: 300, sustain: 1800, rampDown: 300 },
  };

  const ramp = rampConfigs[tier];

  const metricsConfigs: Record<LoadSimConfig['tier'], LoadSimConfig['metrics']> = {
    baseline: {
      sampleIntervalMs: 5000,
      alertThresholds: { p95LatencyMs: 3000, errorRatePercent: 5, sessionLeakCount: 1 },
    },
    growth: {
      sampleIntervalMs: 3000,
      alertThresholds: { p95LatencyMs: 5000, errorRatePercent: 3, sessionLeakCount: 5 },
    },
    surge: {
      sampleIntervalMs: 2000,
      alertThresholds: { p95LatencyMs: 8000, errorRatePercent: 5, sessionLeakCount: 10 },
    },
    viral: {
      sampleIntervalMs: 1000,
      alertThresholds: { p95LatencyMs: 12000, errorRatePercent: 8, sessionLeakCount: 25 },
    },
    stress: {
      sampleIntervalMs: 500,
      alertThresholds: { p95LatencyMs: 20000, errorRatePercent: 15, sessionLeakCount: 50 },
    },
  };

  return {
    tier,
    concurrentUsers,
    rampUpSeconds: ramp.rampUp,
    sustainSeconds: ramp.sustain,
    rampDownSeconds: ramp.rampDown,
    personaMix: getDefaultPersonaMix(),
    contextDistribution: getDefaultContextDist(),
    touchpointDistribution: getDefaultTouchpointDist(),
    metrics: metricsConfigs[tier],
    tooling: tier === 'stress' ? 'custom_cloud_function' : tier === 'viral' ? 'k6' : 'artillery',
  };
}

// --- Cost Estimation ---

const COST_FACTORS = {
  avgTurnsPerSession: 6,
  avgInputTokensPerTurn: 800,
  avgOutputTokensPerTurn: 200,
  medgemmaInputPer1k: 0.0002,
  medgemmaOutputPer1k: 0.0008,
  firestoreReadCost: 0.00006,
  firestoreWriteCost: 0.00018,
  readsPerSession: 15,
  writesPerSession: 8,
  cloudRunCostPerSecond: 0.000024,
  avgSessionDurationSeconds: 45,
};

export function estimateLoadCost(
  config: LoadSimConfig,
): LoadSimRun['results']['costEstimate'] {
  const totalSessions = config.concurrentUsers;
  const totalDurationSeconds =
    config.rampUpSeconds + config.sustainSeconds + config.rampDownSeconds;

  // Sessions over the full test run (rough: concurrent * duration / avg session)
  const estimatedTotalSessions = Math.round(
    (totalSessions * totalDurationSeconds) / COST_FACTORS.avgSessionDurationSeconds,
  );

  const totalTurns = estimatedTotalSessions * COST_FACTORS.avgTurnsPerSession;

  // MedGemma inference
  const inputTokens = totalTurns * COST_FACTORS.avgInputTokensPerTurn;
  const outputTokens = totalTurns * COST_FACTORS.avgOutputTokensPerTurn;
  const medgemmaInferenceCost =
    (inputTokens / 1000) * COST_FACTORS.medgemmaInputPer1k +
    (outputTokens / 1000) * COST_FACTORS.medgemmaOutputPer1k;

  // Firestore
  const totalReads = estimatedTotalSessions * COST_FACTORS.readsPerSession;
  const totalWrites = estimatedTotalSessions * COST_FACTORS.writesPerSession;
  const firestoreReadWriteCost =
    totalReads * COST_FACTORS.firestoreReadCost +
    totalWrites * COST_FACTORS.firestoreWriteCost;

  // Cloud Run compute
  const totalComputeSeconds =
    estimatedTotalSessions * COST_FACTORS.avgSessionDurationSeconds;
  const cloudRunComputeCost =
    totalComputeSeconds * COST_FACTORS.cloudRunCostPerSecond;

  const totalEstimatedMonthlyCost = Math.round(
    (medgemmaInferenceCost + firestoreReadWriteCost + cloudRunComputeCost) * 30 * 100,
  ) / 100;

  return {
    medgemmaInferenceCost: Math.round(medgemmaInferenceCost * 100) / 100,
    firestoreReadWriteCost: Math.round(firestoreReadWriteCost * 100) / 100,
    cloudRunComputeCost: Math.round(cloudRunComputeCost * 100) / 100,
    totalEstimatedMonthlyCost,
  };
}

// --- Simulate Load Run ---

export function simulateLoadRun(config: LoadSimConfig): LoadSimRun {
  const runId = `loadsim-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const now = new Date().toISOString();

  // Base metrics scale with tier
  const tierMultipliers: Record<
    LoadSimConfig['tier'],
    { latency: number; errorRate: number; leaks: number }
  > = {
    baseline: { latency: 1, errorRate: 0.3, leaks: 0 },
    growth: { latency: 1.5, errorRate: 0.8, leaks: 0 },
    surge: { latency: 2.5, errorRate: 1.5, leaks: 0.1 },
    viral: { latency: 4, errorRate: 3, leaks: 0.5 },
    stress: { latency: 8, errorRate: 8, leaks: 2 },
  };

  const mult = tierMultipliers[config.tier];
  const jitter = () => 0.7 + Math.random() * 0.6; // 0.7-1.3x randomness

  const p50 = Math.round(800 * mult.latency * jitter());
  const p95 = Math.round(2200 * mult.latency * jitter());
  const p99 = Math.round(4500 * mult.latency * jitter());

  const totalDuration = config.rampUpSeconds + config.sustainSeconds + config.rampDownSeconds;
  // ~6 turns per session, 45s avg session
  const totalRequests = Math.round(config.concurrentUsers * totalDuration / 45 * 6);
  const errorRate = Math.round(mult.errorRate * jitter() * 100) / 100;
  const totalErrors = Math.round(totalRequests * errorRate / 100);
  const sessionLeaks = Math.round(mult.leaks * jitter() * config.concurrentUsers / 100);
  const protocolAccuracy = Math.round((100 - mult.errorRate * 2) * jitter() * 10) / 10;

  const costEstimate = estimateLoadCost(config);

  return {
    runId,
    brandId: 'hemologix-test',
    config,
    startedAt: now,
    completedAt: new Date(Date.now() + totalDuration * 1000).toISOString(),
    status: 'completed',
    results: {
      peakConcurrency: Math.round(config.concurrentUsers * (0.85 + Math.random() * 0.15)),
      totalRequests,
      totalErrors,
      errorRate,
      latency: { p50, p95, p99 },
      sessionLeaks,
      protocolAccuracyRate: Math.min(100, Math.max(60, protocolAccuracy)),
      breakingPoint: config.tier === 'stress'
        ? Math.round(config.concurrentUsers * (0.6 + Math.random() * 0.3))
        : undefined,
      costEstimate,
    },
  };
}

// --- Config Validation ---

export function validateLoadConfig(config: LoadSimConfig): string[] {
  const errors: string[] = [];
  const tierDef = LOAD_TIERS[config.tier];

  if (!tierDef) {
    errors.push(`Invalid tier: ${config.tier}`);
    return errors;
  }

  if (config.concurrentUsers < tierDef.minUsers) {
    errors.push(
      `concurrentUsers (${config.concurrentUsers}) below tier minimum (${tierDef.minUsers})`,
    );
  }

  if (config.concurrentUsers > tierDef.maxUsers) {
    errors.push(
      `concurrentUsers (${config.concurrentUsers}) above tier maximum (${tierDef.maxUsers})`,
    );
  }

  if (config.rampUpSeconds < 10) {
    errors.push('rampUpSeconds must be at least 10');
  }

  if (config.sustainSeconds < 30) {
    errors.push('sustainSeconds must be at least 30');
  }

  if (config.rampDownSeconds < 10) {
    errors.push('rampDownSeconds must be at least 10');
  }

  // Validate persona mix sums to 100
  const personaMixSum = Object.values(config.personaMix).reduce((a, b) => a + b, 0);
  if (Math.abs(personaMixSum - 100) > 0.01) {
    errors.push(`personaMix must sum to 100 (got ${personaMixSum})`);
  }

  // Validate context distributions sum to 100 each
  const distChecks: { name: string; values: Record<string, number> }[] = [
    { name: 'timeOfDay', values: config.contextDistribution.timeOfDay },
    { name: 'stressLevel', values: config.contextDistribution.stressLevel },
    { name: 'healthKnowledge', values: config.contextDistribution.healthKnowledge },
    { name: 'budgetSensitivity', values: config.contextDistribution.budgetSensitivity },
  ];

  for (const check of distChecks) {
    const sum = Object.values(check.values).reduce((a, b) => a + b, 0);
    if (Math.abs(sum - 100) > 0.01) {
      errors.push(`contextDistribution.${check.name} must sum to 100 (got ${sum})`);
    }
  }

  // Validate touchpoint distribution sums to 100
  const tpSum = Object.values(config.touchpointDistribution).reduce((a, b) => a + b, 0);
  if (Math.abs(tpSum - 100) > 0.01) {
    errors.push(`touchpointDistribution must sum to 100 (got ${tpSum})`);
  }

  // Validate metrics thresholds are positive
  if (config.metrics.sampleIntervalMs < 100) {
    errors.push('metrics.sampleIntervalMs must be at least 100');
  }

  if (config.metrics.alertThresholds.p95LatencyMs < 500) {
    errors.push('metrics.alertThresholds.p95LatencyMs must be at least 500');
  }

  if (config.metrics.alertThresholds.errorRatePercent < 0 || config.metrics.alertThresholds.errorRatePercent > 100) {
    errors.push('metrics.alertThresholds.errorRatePercent must be between 0 and 100');
  }

  // Tooling recommendations
  if (config.tier === 'stress' && config.tooling !== 'custom_cloud_function') {
    errors.push('Stress tier recommended tooling: custom_cloud_function (current: ' + config.tooling + ')');
  }

  return errors;
}
